import { Icon } from './Icons';
import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../firebase';
import { doc, setDoc, getDoc, collection, addDoc, getDocs, orderBy, query, limit, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { loadLatestMetrics, loadAllBloodMarkers } from '../db';

import { apiPost } from '../api';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayStr() { return new Date().toISOString().split('T')[0]; }

function calcBMR(weight, height, age, sex) {
  if (!weight || !height || !age) return null;
  const w = parseFloat(weight), h = parseFloat(height), a = parseFloat(age);
  return sex === 'female'
    ? (10 * w) + (6.25 * h) - (5 * a) - 161
    : (10 * w) + (6.25 * h) - (5 * a) + 5;
}

function calcTDEE(bmr, activityMultiplier) {
  return bmr ? Math.round(bmr * activityMultiplier) : null;
}

function calcAge(dob) {
  if (!dob) return null;
  return Math.floor((Date.now() - new Date(dob).getTime()) / (1000 * 60 * 60 * 24 * 365.25));
}

async function loadNutritionProfile() {
  const user = auth.currentUser;
  if (!user) return null;
  const snap = await getDoc(doc(db, 'users', user.uid, 'latest', 'nutritionProfile'));
  return snap.exists() ? snap.data() : null;
}

async function saveNutritionProfile(data) {
  const user = auth.currentUser;
  if (!user) return;
  await setDoc(doc(db, 'users', user.uid, 'latest', 'nutritionProfile'), { ...data, updatedAt: serverTimestamp() }, { merge: true });
}

async function loadMealPlan() {
  const user = auth.currentUser;
  if (!user) return null;
  const snap = await getDoc(doc(db, 'users', user.uid, 'latest', 'mealPlan'));
  return snap.exists() ? snap.data() : null;
}

async function saveMealPlan(data) {
  const user = auth.currentUser;
  if (!user) return;
  // Save as latest
  await setDoc(doc(db, 'users', user.uid, 'latest', 'mealPlan'), { ...data, savedAt: serverTimestamp() });
  // Also save to history
  await addDoc(collection(db, 'users', user.uid, 'history', 'mealPlans', 'entries'), { ...data, savedAt: serverTimestamp() });
}

async function loadMealPlanHistory() {
  const user = auth.currentUser;
  if (!user) return [];
  const q = query(collection(db, 'users', user.uid, 'history', 'mealPlans', 'entries'), orderBy('savedAt', 'desc'), limit(5));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

async function saveNutritionLog(entry) {
  const user = auth.currentUser;
  if (!user) return;
  const ref = await addDoc(collection(db, 'users', user.uid, 'history', 'nutrition', 'entries'), {
    ...entry, recordedAt: serverTimestamp()
  });
  return ref.id;
}

async function deleteNutritionLog(entryId) {
  const user = auth.currentUser;
  if (!user || !entryId) return;
  await deleteDoc(doc(db, 'users', user.uid, 'history', 'nutrition', 'entries', entryId));
}

// ── Nutrition Gamification ───────────────────────────────────────────────────

const NUTRITION_BADGES = [
  { id: 'first_log', icon: 'edit', label: 'First Entry', desc: 'Logged your first meal', xp: 50 },
  { id: 'log_streak_3', icon: 'flame', label: 'On Track', desc: '3-day logging streak', xp: 100 },
  { id: 'log_streak_7', icon: '', label: 'Week Warrior', desc: '7-day logging streak', xp: 200 },
  { id: 'log_streak_30', icon: 'award', label: 'Habit Formed', desc: '30-day logging streak', xp: 500 },
  { id: 'protein_target', icon: 'dumbbell', label: 'Protein Power', desc: 'Hit protein target 5 days', xp: 150 },
  { id: 'calorie_target', icon: 'target', label: 'Spot On', desc: 'Hit calorie target within 100 kcal', xp: 100 },
  { id: 'logs_10', icon: 'bar-chart', label: 'Data Driven', desc: '10 food entries logged', xp: 100 },
  { id: 'logs_50', icon: 'trending-up', label: 'Consistent', desc: '50 food entries logged', xp: 300 },
];

async function loadNutritionGamification() {
  const user = auth.currentUser;
  if (!user) return null;
  const snap = await getDoc(doc(db, 'users', user.uid, 'latest', 'nutritionGamification'));
  return snap.exists() ? snap.data() : { xp: 0, badges: [], streak: 0, lastLogDate: null, totalLogs: 0, proteinDaysHit: 0 };
}

async function updateNutritionGamification(newEntry, targets, gam) {
  const today = todayStr();
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  let streak = gam.streak || 0;
  if (gam.lastLogDate === yesterday) streak += 1;
  else if (gam.lastLogDate !== today) streak = 1;

  const totalLogs = (gam.totalLogs || 0) + 1;
  let xp = (gam.xp || 0) + 10; // 10 XP per log entry
  const existingBadges = gam.badges || [];
  const newBadges = [];

  const checkBadge = (id, condition) => {
    if (condition && !existingBadges.includes(id)) {
      newBadges.push(id);
      const badge = NUTRITION_BADGES.find(b => b.id === id);
      if (badge) xp += badge.xp;
    }
  };

  checkBadge('first_log', totalLogs >= 1);
  checkBadge('logs_10', totalLogs >= 10);
  checkBadge('logs_50', totalLogs >= 50);
  checkBadge('log_streak_3', streak >= 3);
  checkBadge('log_streak_7', streak >= 7);
  checkBadge('log_streak_30', streak >= 30);

  const updated = { xp, badges: [...existingBadges, ...newBadges], streak, lastLogDate: today, totalLogs, proteinDaysHit: gam.proteinDaysHit || 0 };
  const user = auth.currentUser;
  if (user) await setDoc(doc(db, 'users', user.uid, 'latest', 'nutritionGamification'), { ...updated, updatedAt: serverTimestamp() }, { merge: true });
  return { updated, newBadges };
}

async function loadTodayLog() {
  const user = auth.currentUser;
  if (!user) return [];
  const q = query(collection(db, 'users', user.uid, 'history', 'nutrition', 'entries'), orderBy('recordedAt', 'desc'), limit(50));
  const snap = await getDocs(q);
  const today = todayStr();
  return snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(e => e.date === today);
}

// ── UI Components ─────────────────────────────────────────────────────────────

function Card({ children, style = {} }) {
  return <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', marginBottom: '1rem', ...style }}>{children}</div>;
}

function SLabel({ children, icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.85rem' }}>
      {icon && <Icon name={icon} size={14} color="var(--text3)" />}
      <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em' }}>{children}</div>
    </div>
  );
}

function Inp({ label, value, onChange, type = 'text', placeholder, style = {} }) {
  return (
    <div style={{ marginBottom: '.65rem', ...style }}>
      {label && <label style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: '.3rem' }}>{label}</label>}
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.6rem .8rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.85rem', outline: 'none', boxSizing: 'border-box' }} />
    </div>
  );
}

function Sel({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: '.65rem' }}>
      {label && <label style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: '.3rem' }}>{label}</label>}
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.6rem .8rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.85rem', outline: 'none' }}>
        <option value=''>Select…</option>
        {options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
      </select>
    </div>
  );
}

function TArea({ label, value, onChange, placeholder, rows = 3 }) {
  return (
    <div style={{ marginBottom: '.65rem' }}>
      {label && <label style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: '.3rem' }}>{label}</label>}
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
        style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.6rem .8rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.85rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
    </div>
  );
}

function Btn({ children, onClick, disabled, variant = 'primary', full = false, style = {} }) {
  const variants = {
    primary: { background: 'var(--primary)', color: '#080f18', border: 'none' },
    success: { background: 'var(--success)', color: '#080f18', border: 'none' },
    secondary: { background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border2)' },
    warning: { background: 'var(--warning-dim)', color: 'var(--warning)', border: '1px solid rgba(240,160,80,.3)' },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{ padding: '.65rem 1.25rem', borderRadius: 'var(--radius)', fontFamily: 'var(--font-body)', fontSize: '.82rem', fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? .6 : 1, transition: 'all .2s', width: full ? '100%' : 'auto', ...variants[variant], ...style }}>
      {children}
    </button>
  );
}

function MacroBar({ label, current, target, color }) {
  const pct = target ? Math.min(100, Math.round((current / target) * 100)) : 0;
  const over = current > target;
  return (
    <div style={{ marginBottom: '.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.3rem' }}>
        <span style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</span>
        <span style={{ fontSize: '.72rem', color: over ? 'var(--warning)' : 'var(--text3)' }}>
          {current}g / {target}g {over ? '↑' : ''}
        </span>
      </div>
      <div style={{ height: 6, background: 'var(--bg-elevated)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: over ? 'var(--warning)' : color, borderRadius: 3, transition: 'width .6s ease' }} />
      </div>
    </div>
  );
}

function CalorieRing({ current, target }) {
  const pct = target ? Math.min(100, (current / target) * 100) : 0;
  const over = current > target;
  const color = over ? 'var(--warning)' : pct > 80 ? 'var(--success)' : 'var(--primary)';
  const radius = 44;
  const circ = 2 * Math.PI * radius;
  const dash = (pct / 100) * circ;
  return (
    <div style={{ position: 'relative', width: 110, height: 110, flexShrink: 0 }}>
      <svg width="110" height="110" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="55" cy="55" r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
        <circle cx="55" cy="55" r={radius} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={dash + ' ' + circ} strokeLinecap="round" style={{ transition: 'stroke-dasharray .8s ease' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.2rem', fontWeight: 500, color, lineHeight: 1 }}>{current}</div>
        <div style={{ fontSize: '.6rem', color: 'var(--text3)', marginTop: '.15rem' }}>/ {target} kcal</div>
      </div>
    </div>
  );
}

// ── Setup Flow ────────────────────────────────────────────────────────────────

function NutritionSetup({ onComplete, existingData }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    // Stats (pre-filled from health data)
    weight: existingData?.body?.weight || '',
    height: existingData?.body?.height || '',
    age: existingData?.age || '',
    sex: existingData?.profile?.gender || '',
    goalWeight: '',
    goalPace: '',
    // Lifestyle
    jobType: '',
    exerciseFrequency: existingData?.training?.daysPerWeek || '',
    exerciseType: existingData?.training?.goal || '',
    sleepHours: existingData?.mental?.sleepHours || '',
    stressLevel: '',
    alcoholUnits: existingData?.profile?.alcoholUnits || '',
    // Food preferences
    favouriteMeals: '',
    dislikedFoods: '',
    dietaryRestrictions: '',
    cookingStyle: '',
    foodAdventurousness: '',
    // Snacks
    currentSnacks: '',
    snackReason: '',
    snackPreference: '',
    lateNightSnacking: '',
    // Goals
    primaryGoal: existingData?.training?.goal?.toLowerCase().includes('fat') ? 'fat_loss' : existingData?.training?.goal?.toLowerCase().includes('hyper') ? 'muscle_gain' : 'body_recomp',
  });

  const set = k => v => setProfile(p => ({ ...p, [k]: v }));

  const steps = ['Your Stats', 'Lifestyle', 'Food Preferences', 'Snack Habits', 'Generate Plan'];

  // Calculate targets for preview
  const bmr = calcBMR(profile.weight, profile.height, profile.age, profile.sex);
  const activityMap = {
    sedentary: 1.2, lightly_active: 1.375, moderately_active: 1.55, very_active: 1.725, extremely_active: 1.9
  };
  const activityMultiplier = activityMap[profile.jobType] || 1.375;
  const tdee = calcTDEE(bmr, activityMultiplier);
  const targetCalories = tdee ? (
    profile.primaryGoal === 'fat_loss' ? tdee - 500 :
    profile.primaryGoal === 'muscle_gain' ? tdee + 300 : tdee
  ) : null;
  const proteinTarget = profile.weight ? Math.round(parseFloat(profile.weight) * 2.2) : null;
  const fatTarget = targetCalories ? Math.round(targetCalories * 0.25 / 9) : null;
  const carbTarget = targetCalories && proteinTarget && fatTarget ? Math.round((targetCalories - (proteinTarget * 4) - (fatTarget * 9)) / 4) : null;

  async function handleGenerate() {
    setSaving(true);
    try {
      const profileData = {
        ...profile, bmr: Math.round(bmr), tdee, targetCalories, proteinTarget, carbTarget, fatTarget,
        setupComplete: true, setupDate: todayStr()
      };
      await saveNutritionProfile(profileData);
      onComplete(profileData);
    } catch (e) { alert('Error saving. Please try again.'); }
    setSaving(false);
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.6rem', fontWeight: 600, color: 'var(--text)', marginBottom: '.3rem' }}>Set Up Your Nutrition</h1>
        <p style={{ fontSize: '.8rem', color: 'var(--text3)', fontWeight: 300 }}>Tell Rico about you — it will calculate your targets and build a personalised 7-day meal plan.</p>
      </div>

      <div style={{ display: 'flex', gap: '.4rem', marginBottom: '2rem' }}>
        {steps.map((s, i) => (
          <div key={i} style={{ flex: 1 }}>
            <div style={{ height: 3, borderRadius: 2, background: i <= step ? '#f0a050' : 'var(--border2)', marginBottom: '.4rem', transition: 'background .3s' }} />
            <div style={{ fontSize: '.58rem', color: i === step ? '#f0a050' : 'var(--text3)', fontWeight: i === step ? 600 : 300, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s}</div>
          </div>
        ))}
      </div>

      {/* Step 0: Stats */}
      {step === 0 && (
        <Card>
          <SLabel icon="bar-chart">Your Stats</SLabel>
          <div style={{ padding: '.75rem 1rem', background: 'rgba(240,160,80,0.06)', border: '1px solid rgba(240,160,80,0.15)', borderRadius: 'var(--radius)', marginBottom: '1rem', fontSize: '.75rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6 }}>
            <Icon name="info" size={14} /> We've pre-filled what we know from your health profile. Check and update anything that's changed.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '.75rem' }}>
            <Inp label="Current Weight (kg)" type="number" value={profile.weight} onChange={set('weight')} placeholder="e.g. 85" />
            <Inp label="Height (cm)" type="number" value={profile.height} onChange={set('height')} placeholder="e.g. 178" />
            <Inp label="Age" type="number" value={profile.age} onChange={set('age')} placeholder="e.g. 34" />
            <Sel label="Biological Sex" value={profile.sex} onChange={set('sex')} options={[{value:'male',label:'Male'},{value:'female',label:'Female'}]} />
            <Inp label="Goal Weight (kg)" type="number" value={profile.goalWeight} onChange={set('goalWeight')} placeholder="e.g. 78 (or leave blank)" />
            <Sel label="Primary Goal" value={profile.primaryGoal} onChange={set('primaryGoal')} options={[
              {value:'fat_loss',label:'Fat loss'},
              {value:'muscle_gain',label:'Muscle gain / Bulk'},
              {value:'body_recomp',label:'Body recomposition'},
              {value:'maintenance',label:'Maintain current weight'},
              {value:'general_health',label:'General health & energy'},
            ]} />
          </div>
          <Sel label="How quickly do you want to reach your goal?" value={profile.goalPace} onChange={set('goalPace')} options={[
            {value:'steady',label:'Steady and sustainable (1 lb/week)'},
            {value:'moderate',label:'Moderate pace (1-1.5 lb/week)'},
            {value:'aggressive',label:'As fast as safely possible'},
          ]} />
          {bmr && tdee && (
            <div style={{ padding: '.85rem 1rem', background: 'rgba(240,160,80,0.06)', border: '1px solid rgba(240,160,80,0.2)', borderRadius: 'var(--radius)', marginTop: '.5rem' }}>
              <div style={{ fontSize: '.72rem', fontWeight: 600, color: '#f0a050', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.4rem' }}>Estimated targets (preview)</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))', gap: '.5rem' }}>
                {[['BMR',Math.round(bmr)+' kcal'],['TDEE',tdee+' kcal'],['Target',targetCalories ? targetCalories+' kcal' : '—'],['Protein',proteinTarget ? proteinTarget+'g' : '—']].map(([l,v]) => (
                  <div key={l} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '.65rem', color: 'var(--text3)' }}>{l}</div>
                    <div style={{ fontSize: '.88rem', fontWeight: 500, color: '#f0a050' }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Step 1: Lifestyle */}
      {step === 1 && (
        <Card>
          <SLabel icon="activity">Your Lifestyle</SLabel>
          <Sel label="Job Type" value={profile.jobType} onChange={set('jobType')} options={[
            {value:'sedentary',label:'Desk job / mostly sitting'},
            {value:'lightly_active',label:'Light physical activity (standing, walking)'},
            {value:'moderately_active',label:'Moderately physical (on feet most of day)'},
            {value:'very_active',label:'Very physical (manual labour, delivery, etc)'},
            {value:'extremely_active',label:'Extremely physical (heavy manual labour)'},
          ]} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '.75rem' }}>
            <Inp label="Exercise sessions per week" type="number" value={profile.exerciseFrequency} onChange={set('exerciseFrequency')} placeholder="e.g. 4" />
            <Inp label="Exercise type" value={profile.exerciseType} onChange={set('exerciseType')} placeholder="e.g. Weight training, running" />
            <Sel label="Average sleep" value={profile.sleepHours} onChange={set('sleepHours')} options={['Under 5 hours','5-6 hours','6-7 hours','7-8 hours','8+ hours']} />
            <Sel label="Stress levels" value={profile.stressLevel} onChange={set('stressLevel')} options={[{value:'low',label:'Low'},{value:'moderate',label:'Moderate'},{value:'high',label:'High — affects sleep/appetite'}]} />
          </div>
          <Inp label="Weekly alcohol units (0 if none)" type="number" value={profile.alcoholUnits} onChange={set('alcoholUnits')} placeholder="e.g. 6" />
        </Card>
      )}

      {/* Step 2: Food Preferences */}
      {step === 2 && (
        <Card>
          <SLabel icon="utensils">Food Preferences</SLabel>
          <TArea label="Your top 5 favourite meals or dishes (any cuisine)" value={profile.favouriteMeals} onChange={set('favouriteMeals')} placeholder="e.g. Sunday roast, chicken tikka masala, pasta carbonara, fish tacos, beef stir fry" rows={3} />
          <TArea label="Foods you absolutely hate or would never eat" value={profile.dislikedFoods} onChange={set('dislikedFoods')} placeholder="e.g. Mushrooms, olives, blue cheese" rows={2} />
          <TArea label="Dietary restrictions or allergies" value={profile.dietaryRestrictions} onChange={set('dietaryRestrictions')} placeholder="e.g. No pork, lactose intolerant, gluten free, vegetarian" rows={2} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '.75rem' }}>
            <Sel label="Cooking style" value={profile.cookingStyle} onChange={set('cookingStyle')} options={[
              {value:'scratch',label:'Cook from scratch'},
              {value:'quick',label:'Quick meals (under 30 min)'},
              {value:'batch',label:'Meal prep / batch cooking'},
              {value:'mixed',label:'Mix of all three'},
            ]} />
            <Sel label="Food adventurousness (1-10)" value={profile.foodAdventurousness} onChange={set('foodAdventurousness')} options={['1 - Very safe eater','2','3','4','5 - Moderately adventurous','6','7','8','9','10 - Will try anything']} />
          </div>
        </Card>
      )}

      {/* Step 3: Snack Habits */}
      {step === 3 && (
        <Card>
          <SLabel icon="">Snack Habits</SLabel>
          <TArea label="What snacks do you currently reach for?" value={profile.currentSnacks} onChange={set('currentSnacks')} placeholder="e.g. Crisps, biscuits, chocolate bar, fruit, nuts" rows={2} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '.75rem' }}>
            <Sel label="You snack mostly out of…" value={profile.snackReason} onChange={set('snackReason')} options={[
              {value:'hunger',label:'Genuine hunger'},
              {value:'boredom',label:'Boredom'},
              {value:'habit',label:'Habit / routine'},
              {value:'stress',label:'Stress or emotion'},
              {value:'mixed',label:'Mix of reasons'},
            ]} />
            <Sel label="Snack preference" value={profile.snackPreference} onChange={set('snackPreference')} options={[
              {value:'sweet',label:'Sweet'},
              {value:'savoury',label:'Savoury'},
              {value:'both',label:'Both'},
              {value:'neither',label:'Not a big snacker'},
            ]} />
          </div>
          <Sel label="Late night snacking?" value={profile.lateNightSnacking} onChange={set('lateNightSnacking')} options={[
            {value:'never',label:'Never'},
            {value:'occasionally',label:'Occasionally'},
            {value:'most_nights',label:'Most nights'},
            {value:'every_night',label:'Every night without fail'},
          ]} />
        </Card>
      )}

      {/* Step 4: Generate */}
      {step === 4 && (
        <Card>
          <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}><Icon name="apple" size={14} /></div>
            <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.4rem', color: 'var(--text)', marginBottom: '.75rem' }}>Ready to build your plan</div>
            <p style={{ fontSize: '.82rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.7, maxWidth: 440, margin: '0 auto 1.5rem' }}>
              Rico Nutrition will calculate your personalised calorie and macro targets, then build a complete 7-day meal plan with recipes, snack swaps and supplement recommendations — all based on your preferences, lifestyle and health data.
            </p>
            {targetCalories && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))', gap: '.5rem', maxWidth: 420, margin: '0 auto 1.5rem' }}>
                {[['Target Calories',targetCalories+' kcal','#f0a050'],['Protein',proteinTarget+'g','var(--success)'],['Carbs',carbTarget+'g','var(--primary)'],['Fat',fatTarget+'g','#a78bfa']].map(([l,v,c]) => (
                  <div key={l} style={{ padding: '.75rem', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-lg)', textAlign: 'center' }}>
                    <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', color: c, fontWeight: 500 }}>{v}</div>
                    <div style={{ fontSize: '.65rem', color: 'var(--text3)', marginTop: '.2rem' }}>{l}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      <div style={{ display: 'flex', gap: '.75rem', justifyContent: 'flex-end' }}>
        {step > 0 && <Btn variant="secondary" onClick={() => setStep(s => s-1)}>← Back</Btn>}
        {step < steps.length - 1 && <Btn onClick={() => setStep(s => s+1)} style={{ background: '#f0a050', color: '#080f18' }}>Continue →</Btn>}
        {step === steps.length - 1 && <Btn onClick={handleGenerate} disabled={saving} style={{ background: '#f0a050', color: '#080f18' }}>{saving ? 'Saving…' : 'Save & Generate Meal Plan →'}</Btn>}
      </div>
    </div>
  );
}

// ── Meal Plan View ────────────────────────────────────────────────────────────

function MealPlanView({ mealPlan, nutritionProfile, onRegenerate, generating }) {
  const [selectedDay, setSelectedDay] = useState(0);
  const [expandedMeal, setExpandedMeal] = useState(null);

  if (!mealPlan || !mealPlan.days) return (
    <div style={{ textAlign: 'center', padding: '3rem', maxWidth: 480, margin: '0 auto' }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}><Icon name="utensils" size={14} /></div>
      <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', color: 'var(--text)', marginBottom: '.75rem' }}>No meal plan yet</div>
      <p style={{ fontSize: '.82rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.7, marginBottom: '1.5rem' }}>
        Rico Nutrition will build a personalised 7-day meal plan with detailed recipes, based on your food preferences, calorie targets and health data.
      </p>
      {generating ? (
        <div>
          <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid rgba(240,160,80,0.2)', borderTop: '3px solid #f0a050', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
          <div style={{ fontSize: '.82rem', color: 'var(--text3)' }}>Building your 7-day meal plan… (~30 seconds)</div>
        </div>
      ) : (
        <Btn onClick={onRegenerate} style={{ background: '#f0a050', color: '#080f18' }}>Generate My Meal Plan →</Btn>
      )}
      <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );

  const day = mealPlan.days[selectedDay];
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.2rem' }}>My 7-Day Meal Plan</h2>
          <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300 }}>
            {nutritionProfile.targetCalories} kcal · {nutritionProfile.proteinTarget}g protein · {nutritionProfile.carbTarget}g carbs · {nutritionProfile.fatTarget}g fat
          </p>
        </div>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          {generating ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', color: 'var(--text3)', fontSize: '.78rem' }}>
              <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(240,160,80,0.3)', borderTop: '2px solid #f0a050', animation: 'spin 0.8s linear infinite' }} />
              Regenerating…
            </div>
          ) : (
            <Btn variant="secondary" onClick={onRegenerate}>↻ Regenerate Plan</Btn>
          )}
        </div>
      </div>

      {/* Targets summary */}
      {nutritionProfile.tdee && (
        <div style={{ padding: '.85rem 1.25rem', background: 'rgba(240,160,80,0.06)', border: '1px solid rgba(240,160,80,0.15)', borderRadius: 'var(--radius-lg)', marginBottom: '1.25rem', display: 'flex', gap: '1.5rem', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
          {[['BMR', nutritionProfile.bmr+' kcal'], ['TDEE', nutritionProfile.tdee+' kcal'], ['Daily Target', nutritionProfile.targetCalories+' kcal'], ['Deficit/Surplus', (nutritionProfile.targetCalories - nutritionProfile.tdee)+' kcal']].map(([l,v]) => (
            <div key={l} style={{ flexShrink: 0 }}>
              <div style={{ fontSize: '.65rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap' }}>{l}</div>
              <div style={{ fontSize: '.88rem', fontWeight: 500, color: '#f0a050', whiteSpace: 'nowrap' }}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Day selector */}
      <div style={{ display: 'flex', gap: '.4rem', marginBottom: '1.25rem', overflowX: 'auto', paddingBottom: '.3rem' }}>
        {mealPlan.days.map((d, i) => {
          const target = nutritionProfile?.targetCalories || 0;
          const diff = target && d.totalCalories ? Math.abs(d.totalCalories - target) / target : 0;
          const offTarget = diff > 0.08;
          return (
            <button key={i} onClick={() => { setSelectedDay(i); setExpandedMeal(null); }} style={{
              padding: '.5rem .85rem', borderRadius: 'var(--radius)', border: '1px solid', flexShrink: 0,
              borderColor: selectedDay === i ? '#f0a050' : offTarget ? 'rgba(240,160,80,0.3)' : 'var(--border2)',
              background: selectedDay === i ? 'rgba(240,160,80,0.1)' : 'transparent',
              color: selectedDay === i ? '#f0a050' : 'var(--text3)',
              fontSize: '.75rem', cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'center'
            }}>
              <div style={{ fontWeight: 600 }}>{dayNames[i]?.slice(0,3)}</div>
              <div style={{ fontSize: '.65rem', color: offTarget ? 'var(--warning)' : 'inherit', opacity: offTarget ? 1 : 0.8 }}>
                {d.totalCalories} kcal{offTarget ?  <Icon name="warning" size={14} /> : ''}
              </div>
            </button>
          );
        })}
      </div>

      {day && (
        <div>
          {/* Day header */}
          <div style={{ padding: '.85rem 1.1rem', background: 'rgba(240,160,80,0.06)', border: '1px solid rgba(240,160,80,0.15)', borderRadius: 'var(--radius-lg)', marginBottom: '1rem' }}>
            <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.1rem', color: '#f0a050', marginBottom: '.2rem' }}>{day.theme || dayNames[selectedDay]}</div>
            <div style={{ display: 'flex', gap: '1.5rem', fontSize: '.75rem', color: 'var(--text3)', flexWrap: 'wrap' }}>
              <span><Icon name="flame" size={14} /> {day.totalCalories} kcal</span>
              <span><Icon name="utensils" size={14} /> {day.totalProtein}g protein</span>
              <span><Icon name="utensils" size={14} /> {(day.totalCarbs || (day.meals||[]).reduce((s,m)=>s+(m.carbs||0),0))}g carbs</span>
              <span><Icon name="utensils" size={14} /> {(day.totalFat || (day.meals||[]).reduce((s,m)=>s+(m.fat||0),0))}g fat</span>
            </div>
          </div>

          {/* Meals */}
          {(day.meals || []).map((meal, mi) => (
            <div key={mi} style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', marginBottom: '.65rem', overflow: 'hidden' }}>
              <div onClick={() => setExpandedMeal(expandedMeal === mi ? null : mi)}
                style={{ padding: '1rem 1.25rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.85rem' }}>
                  <div style={{ fontSize: '1.3rem' }}>{meal.mealType === 'breakfast' ? <Icon name="sun" size={14} /> : meal.mealType === 'lunch' ? <Icon name="sun" size={14} /> : meal.mealType === 'dinner' ? <Icon name="moon" size={14} /> : meal.mealType === 'snack' ? <Icon name="apple" size={14} /> : ''}</div>
                  <div>
                    <div style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{meal.mealType}</div>
                    <div style={{ fontSize: '.9rem', fontWeight: 500, color: 'var(--text)', marginTop: '.1rem' }}>{meal.name}</div>
                    <div style={{ fontSize: '.72rem', color: 'var(--text3)', marginTop: '.1rem' }}>
                      {meal.calories} kcal · P: {meal.protein}g · C: {meal.carbs}g · F: {meal.fat}g
                      {meal.batchCook && ' ·  Great for batch cooking'}
                      {meal.treatMeal && ' · 🎉 Treat meal'}
                    </div>
                  </div>
                </div>
                <span style={{ color: 'var(--text3)', fontSize: '.7rem' }}>{expandedMeal === mi ? '▲' : '▼'}</span>
              </div>

              {expandedMeal === mi && (
                <div style={{ borderTop: '1px solid var(--border2)', padding: '1.25rem', background: 'var(--bg-elevated)' }}>
                  {meal.description && <p style={{ fontSize: '.82rem', color: 'var(--text2)', fontWeight: 300, lineHeight: 1.6, marginBottom: '1rem' }}>{meal.description}</p>}

                  {meal.ingredients && meal.ingredients.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '.7rem', fontWeight: 600, color: '#f0a050', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.5rem' }}>Ingredients</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.3rem' }}>
                        {meal.ingredients.map((ing, k) => (
                          <div key={k} style={{ fontSize: '.78rem', color: 'var(--text2)', padding: '.3rem 0', borderBottom: '0.5px solid var(--border2)', display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontWeight: 300 }}>{ing.item}</span>
                            <span style={{ color: 'var(--text3)', flexShrink: 0, marginLeft: '.5rem' }}>{ing.amount}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {meal.method && meal.method.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '.7rem', fontWeight: 600, color: '#f0a050', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.5rem' }}>Method</div>
                      {meal.method.map((step, k) => (
                        <div key={k} style={{ display: 'flex', gap: '.75rem', marginBottom: '.5rem' }}>
                          <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(240,160,80,0.15)', border: '1px solid rgba(240,160,80,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.65rem', fontWeight: 700, color: '#f0a050', flexShrink: 0 }}>{k+1}</div>
                          <div style={{ fontSize: '.8rem', color: 'var(--text2)', fontWeight: 300, lineHeight: 1.6 }}>{step}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {meal.prepTime && (
                    <div style={{ fontSize: '.72rem', color: 'var(--text3)', display: 'flex', gap: '1rem' }}>
                      {meal.prepTime && <span>⏱ Prep: {meal.prepTime}</span>}
                      {meal.cookTime && <span><Icon name="flame" size={14} /> Cook: {meal.cookTime}</span>}
                      {meal.servings && <span><Icon name="user" size={14} /> Serves: {meal.servings}</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Day snack swaps */}
          {day.snackSwaps && day.snackSwaps.length > 0 && (
            <Card style={{ background: 'rgba(240,160,80,0.04)', border: '1px solid rgba(240,160,80,0.15)' }}>
              <SLabel icon="">Smart Snack Swaps</SLabel>
              {day.snackSwaps.map((swap, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '.75rem', padding: '.6rem 0', borderBottom: '0.5px solid var(--border2)' }}>
                  <span style={{ fontSize: '.8rem', color: 'var(--danger)', textDecoration: 'line-through', flex: 1 }}>{swap.original} ({swap.originalCals} kcal)</span>
                  <span style={{ color: 'var(--text3)' }}>→</span>
                  <span style={{ fontSize: '.8rem', color: 'var(--success)', flex: 1 }}>{swap.swap} ({swap.swapCals} kcal)</span>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {/* Supplements */}
      {mealPlan.supplements && mealPlan.supplements.length > 0 && selectedDay === 0 && (
        <Card style={{ marginTop: '1rem', background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.15)' }}>
          <SLabel icon="pill">Supplement Recommendations</SLabel>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px,1fr))', gap: '.75rem' }}>
            {mealPlan.supplements.map((sup, i) => (
              <div key={i} style={{ padding: '.85rem 1rem', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border2)' }}>
                <div style={{ fontSize: '.82rem', fontWeight: 500, color: '#a78bfa', marginBottom: '.3rem' }}>{sup.name}</div>
                <div style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.5, marginBottom: '.4rem' }}>{sup.reason}</div>
                <div style={{ fontSize: '.7rem', color: 'var(--text2)' }}>Dose: {sup.dose} · {sup.timing}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Personal rules */}
      {mealPlan.personalRules && mealPlan.personalRules.length > 0 && selectedDay === 0 && (
        <Card style={{ marginTop: '1rem' }}>
          <SLabel icon="clipboard">Your Personal Fat Loss Rules</SLabel>
          {mealPlan.personalRules.map((rule, i) => (
            <div key={i} style={{ display: 'flex', gap: '.75rem', padding: '.6rem 0', borderBottom: '0.5px solid var(--border2)' }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'rgba(240,160,80,0.15)', border: '1px solid rgba(240,160,80,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.65rem', fontWeight: 700, color: '#f0a050', flexShrink: 0 }}>{i+1}</div>
              <div style={{ fontSize: '.8rem', color: 'var(--text2)', fontWeight: 300, lineHeight: 1.6 }}>{rule}</div>
            </div>
          ))}
        </Card>
      )}
      <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}

// ── Food Log ──────────────────────────────────────────────────────────────────

function FoodLog({ nutritionProfile }) {
  const [entries, setEntries] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [interpreting, setInterpreting] = useState(false);
  const [error, setError] = useState('');
  const [gam, setGam] = useState(null);
  const [toast, setToast] = useState(null);
  const [photoMode, setPhotoMode] = React.useState(false);
  const [photoPreview, setPhotoPreview] = React.useState(null);
  const photoInputRef = React.useRef(null);

  useEffect(() => {
    loadTodayLog().then(setEntries);
    loadNutritionGamification().then(setGam);
  }, []);

  const totalCalories = entries.reduce((s, e) => s + (parseFloat(e.calories) || 0), 0);
  const totalProtein = entries.reduce((s, e) => s + (parseFloat(e.protein) || 0), 0);
  const totalCarbs = entries.reduce((s, e) => s + (parseFloat(e.carbs) || 0), 0);
  const totalFat = entries.reduce((s, e) => s + (parseFloat(e.fat) || 0), 0);

  const remaining = Math.max(0, (nutritionProfile.targetCalories || 2000) - Math.round(totalCalories));

  async function handlePhotoSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setPhotoPreview(ev.target.result); setPhotoMode(true); };
    reader.readAsDataURL(file);
  }

  async function handlePhotoLog() {
    if (!photoPreview) return;
    setInterpreting(true);
    setError('');
    try {
      const data = await apiPost('/api/interpret-food-image', {
        imageData: photoPreview,
        targetCalories: nutritionProfile.targetCalories
      });
      if (data.items && data.items.length > 0) {
        const entry = {
          date: todayStr(),
          description: data.description || 'Photo logged meal',
          items: data.items,
          calories: data.totalCalories,
          protein: data.totalProtein,
          carbs: data.totalCarbs,
          fat: data.totalFat,
          mealType: data.mealType || 'meal',
          fromPhoto: true
        };
        await saveNutritionLog(entry);
        setEntries(prev => [entry, ...prev]);
        setPhotoPreview(null);
        setPhotoMode(false);
        setInput(data.description || '');
      }
    } catch(e) {
      setError('Could not analyse photo. Try describing the meal in text instead.');
    }
    setInterpreting(false);
  }

  async function handleLog() {
    if (!input.trim()) return;
    setInterpreting(true);
    setError('');
    try {
      const data = await apiPost('/api/interpret-food', { description: input, targetCalories: nutritionProfile.targetCalories });
      if (data.items && data.items.length > 0) {
        const entry = {
          date: todayStr(),
          description: input,
          items: data.items,
          calories: data.totalCalories,
          protein: data.totalProtein,
          carbs: data.totalCarbs,
          fat: data.totalFat,
          mealType: data.mealType || 'meal'
        };
        await saveNutritionLog(entry);
        setEntries(prev => {
          const updated = [entry, ...prev];
          // Check if protein target hit today
          if (gam) {
            updateNutritionGamification(entry, { protein: nutritionProfile.proteinTarget }, gam).then(({ updated: updatedGam, newBadges }) => {
              setGam(updatedGam);
              if (newBadges.length > 0) setToast(newBadges);
            });
          }
          return updated;
        });
        setInput('');
      }
    } catch (e) {
      console.error('Food log error:', e.message);
      if (e.message === 'RATE_LIMIT') setError('Too many requests. Please wait a moment.');
      else if (e.message === 'UNAUTHORISED') setError('Session expired — please refresh the page and log in again.');
      else setError('Could not log food: ' + (e.message || 'Unknown error. Check you are logged in.'));
    }
    setInterpreting(false);
  }

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      {toast && (
        <div style={{ position: 'fixed', top: 80, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
          {toast.map(badgeId => {
            const badge = NUTRITION_BADGES.find(b => b.id === badgeId);
            if (!badge) return null;
            return (
              <div key={badgeId} onClick={() => setToast(null)} style={{ background: 'rgba(240,160,80,0.95)', borderRadius: 'var(--radius-lg)', padding: '.85rem 1.1rem', color: '#080f18', display: 'flex', alignItems: 'center', gap: '.6rem', cursor: 'pointer', boxShadow: '0 8px 32px rgba(240,160,80,0.3)' }}>
                <Icon name={badge.icon} size={16} />
                <div>
                  <div style={{ fontSize: '.78rem', fontWeight: 700 }}>Badge Earned! +{badge.xp} XP</div>
                  <div style={{ fontSize: '.72rem' }}>{badge.label} — {badge.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.2rem' }}>Today's Food Log</h2>
          <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300 }}>Describe what you've eaten in plain English — Rico will extract the calories and macros.</p>
        </div>
        {gam && (
          <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
            {gam.streak > 1 && <div style={{ padding: '.3rem .75rem', borderRadius: '20px', background: 'rgba(240,160,80,0.1)', border: '1px solid rgba(240,160,80,0.2)', fontSize: '.72rem', fontWeight: 600, color: '#f0a050' }}>{gam.streak}<Icon name="flame" size={14} /> Streak</div>}
            <div style={{ padding: '.3rem .75rem', borderRadius: '20px', background: 'rgba(240,160,80,0.1)', border: '1px solid rgba(240,160,80,0.2)', fontSize: '.72rem', fontWeight: 600, color: '#f0a050' }}>{gam.xp} XP</div>
            <div style={{ padding: '.3rem .75rem', borderRadius: '20px', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', fontSize: '.72rem', color: 'var(--text3)' }}>{gam.badges?.length || 0} badges</div>
          </div>
        )}
      </div>

      {/* Daily progress */}
      <Card>
        <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%', maxWidth: 110 }}>
            <CalorieRing current={Math.round(totalCalories)} target={nutritionProfile.targetCalories || 2000} />
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <MacroBar label="Protein" current={Math.round(totalProtein)} target={nutritionProfile.proteinTarget || 150} color="var(--success)" />
            <MacroBar label="Carbs" current={Math.round(totalCarbs)} target={nutritionProfile.carbTarget || 200} color="var(--primary)" />
            <MacroBar label="Fat" current={Math.round(totalFat)} target={nutritionProfile.fatTarget || 65} color="#a78bfa" />
          </div>
        </div>
        <div style={{ marginTop: '1rem', padding: '.6rem .85rem', background: remaining > 0 ? 'var(--bg-elevated)' : 'var(--warning-dim)', borderRadius: 'var(--radius)', fontSize: '.78rem', color: remaining > 0 ? 'var(--text3)' : 'var(--warning)', textAlign: 'center' }}>
          {remaining > 0 ? `${remaining} kcal remaining today` : `${Math.abs(remaining)} kcal over target`}
        </div>
      </Card>

      {/* Food input */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.75rem' }}>
          <SLabel icon="edit">Log Food</SLabel>
          <div style={{ display: 'flex', gap: '.4rem' }}>
            <button onClick={() => setPhotoMode(false)} style={{ padding: '.3rem .75rem', borderRadius: 'var(--radius)', border: '1px solid', borderColor: !photoMode ? 'var(--primary)' : 'var(--border2)', background: !photoMode ? 'rgba(0,174,239,0.08)' : 'transparent', color: !photoMode ? 'var(--primary)' : 'var(--text3)', fontSize: '.7rem', cursor: 'pointer', fontFamily: 'var(--font-body)', display: 'flex', alignItems: 'center', gap: '.3rem' }}>
              <Icon name="edit" size={12} /> Text
            </button>
            <button onClick={() => { setPhotoMode(true); photoInputRef.current?.click(); }} style={{ padding: '.3rem .75rem', borderRadius: 'var(--radius)', border: '1px solid', borderColor: photoMode ? 'var(--primary)' : 'var(--border2)', background: photoMode ? 'rgba(0,174,239,0.08)' : 'transparent', color: photoMode ? 'var(--primary)' : 'var(--text3)', fontSize: '.7rem', cursor: 'pointer', fontFamily: 'var(--font-body)', display: 'flex', alignItems: 'center', gap: '.3rem' }}>
              <Icon name="camera" size={12} /> Photo
            </button>
          </div>
        </div>
        <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoSelect} style={{ display: 'none' }} />

        {photoMode && photoPreview ? (
          <div>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', marginBottom: '.75rem' }}>
              <img src={photoPreview} alt="Food" style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '.78rem', color: 'var(--text3)', marginBottom: '.5rem' }}>Rico will analyse this photo and estimate the calories and macros.</p>
                <div style={{ display: 'flex', gap: '.5rem' }}>
                  <Btn onClick={handlePhotoLog} disabled={interpreting} style={{ background: '#f0a050', color: '#080f18' }}>
                    {interpreting ? 'Analysing…' : 'Analyse Photo'}
                  </Btn>
                  <Btn variant="secondary" onClick={() => { setPhotoPreview(null); setPhotoMode(false); }}>Cancel</Btn>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '.6rem', alignItems: 'flex-end' }}>
            <textarea value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleLog(); } }}
              placeholder="Describe what you ate — e.g. 'Two scrambled eggs on wholegrain toast with a black coffee'"
              rows={2}
              style={{ flex: 1, resize: 'none', padding: '.75rem 1rem', borderRadius: 'var(--radius-lg)', lineHeight: 1.5, fontFamily: 'var(--font-body)', fontSize: '.85rem', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', color: 'var(--text)', outline: 'none' }} />
            <Btn onClick={handleLog} disabled={!input.trim() || interpreting} style={{ background: '#f0a050', color: '#080f18', height: 46, flexShrink: 0 }}>
              {interpreting ? '…' : 'Log'}
            </Btn>
          </div>
        )}
        {error && <div style={{ marginTop: '.5rem', fontSize: '.75rem', color: 'var(--danger)' }}>{error}</div>}
        <div style={{ marginTop: '.5rem', fontSize: '.68rem', color: 'var(--text3)', fontWeight: 300 }}>
          Rico uses AI to interpret your description and estimate calories and macros. Estimates may vary ±10%.
        </div>
      </Card>

      {/* Log entries */}
      {entries.length > 0 ? (
        <div>
          <div style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>Today's entries</div>
          {entries.map((entry, i) => (
            <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem', marginBottom: '.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '.82rem', color: 'var(--text)', fontWeight: 400, marginBottom: '.2rem' }}>{entry.description}</div>
                {entry.items && entry.items.length > 0 && (
                  <div style={{ fontSize: '.7rem', color: 'var(--text3)', fontWeight: 300 }}>
                    {entry.items.map((item, j) => `${item.name} (${item.calories} kcal)`).join(' · ')}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: '.88rem', fontWeight: 600, color: '#f0a050' }}>{Math.round(entry.calories)} kcal</div>
                <div style={{ fontSize: '.68rem', color: 'var(--text3)' }}>P:{Math.round(entry.protein)}g C:{Math.round(entry.carbs)}g F:{Math.round(entry.fat)}g</div>
                {entry.id && <button onClick={async () => {
                  await deleteNutritionLog(entry.id);
                  setEntries(prev => prev.filter(e => e.id !== entry.id));
                }} style={{ marginTop: '.3rem', padding: '.15rem .5rem', borderRadius: '4px', background: 'none', border: '1px solid rgba(240,80,80,.2)', color: 'var(--danger)', fontSize: '.65rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>× Remove</button>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text3)', fontSize: '.82rem' }}>
          No food logged yet today. Start by describing your first meal above.
        </div>
      )}
    </div>
  );
}

// ── Rico Nutrition Chat ───────────────────────────────────────────────────────

function RicoNutrition({ nutritionProfile, bloodData, mealPlan, todayEntries }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const SUGGESTIONS = [
    'Review my meal plan and suggest improvements',
    "I'm not hitting my protein target — what should I add?",
    'Give me 5 high-protein breakfast ideas',
    'How does my diet affect my blood results?',
    "I had a bad day — how do I get back on track?",
  ];

  useEffect(() => {
    const name = nutritionProfile?.name || '';
    setMessages([{ role: 'assistant', content: `Hi${name ? ', ' + name : ''} — I'm Rico Nutrition, your personal nutritionist. I have your meal plan, calorie targets and health data loaded.\n\nI can review your plan, suggest meal ideas, explain how your diet is affecting your blood markers, or help you stay on track. What do you need?` }]);
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  async function sendMessage(text) {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');
    const newMessages = [...messages, { role: 'user', content: msg }];
    setMessages(newMessages);
    setLoading(true);
    try {
      const todayLog = (todayEntries || []).map(e => e.description + ' (' + (e.calories||0) + ' kcal, ' + (e.protein||0) + 'g protein)').join('; ');
      const totalCalsToday = Math.round((todayEntries || []).reduce((s,e) => s + (parseFloat(e.calories)||0), 0));
      const totalProtToday = Math.round((todayEntries || []).reduce((s,e) => s + (parseFloat(e.protein)||0), 0));
      const data = await apiPost('/api/nutrition-chat', { messages: newMessages.slice(-20).map(m => ({ role: m.role, content: m.content })), nutritionProfile, bloodData, mealPlan, todayLog, totalCalsToday, totalProtToday });
      setMessages(prev => [...prev, { role: 'assistant', content: data.content }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection issue. Please try again.' }]);
    }
    setLoading(false);
    inputRef.current?.focus();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px - 8rem)', maxHeight: 780, maxWidth: 860, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.85rem', marginBottom: '1.25rem', flexShrink: 0 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(240,160,80,0.15)', border: '1px solid rgba(240,160,80,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-head)', fontSize: '.9rem', fontWeight: 500, color: '#f0a050' }}>RN</div>
        <div>
          <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.2rem', color: 'var(--text)' }}>Rico Nutrition</div>
          <div style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>Your personal nutritionist · meal plan & health data loaded</div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '.85rem', marginBottom: '1rem' }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', gap: '.6rem', alignItems: 'flex-end' }}>
            {msg.role === 'assistant' && <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(240,160,80,0.15)', border: '1px solid rgba(240,160,80,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.6rem', fontWeight: 600, color: '#f0a050', flexShrink: 0 }}>RN</div>}
            <div style={{ maxWidth: '78%', background: msg.role === 'user' ? 'rgba(240,160,80,0.1)' : 'var(--bg-card)', border: msg.role === 'user' ? '1px solid rgba(240,160,80,0.25)' : '1px solid var(--border2)', borderRadius: msg.role === 'user' ? 'var(--radius-lg) 4px var(--radius-lg) var(--radius-lg)' : '4px var(--radius-lg) var(--radius-lg) var(--radius-lg)', padding: '.9rem 1.1rem', fontSize: '.83rem', lineHeight: 1.7, color: 'var(--text)', fontWeight: 300, whiteSpace: 'pre-wrap' }}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', gap: '.6rem', alignItems: 'flex-end' }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(240,160,80,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.6rem', fontWeight: 600, color: '#f0a050', flexShrink: 0 }}>RN</div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: '4px var(--radius-lg) var(--radius-lg) var(--radius-lg)', padding: '.9rem 1.2rem', display: 'flex', gap: '.3rem' }}>
              {[0,1,2].map(j => <div key={j} style={{ width: 6, height: 6, borderRadius: '50%', background: '#f0a050', opacity: .5, animation: `bounce 1.2s ${j*.2}s infinite` }} />)}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {messages.length <= 1 && (
        <div style={{ flexShrink: 0, marginBottom: '.85rem', display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
          {SUGGESTIONS.map(s => (
            <button key={s} onClick={() => sendMessage(s)} style={{ padding: '.4rem .85rem', borderRadius: '20px', background: 'var(--bg-card)', border: '1px solid var(--border2)', color: 'var(--text2)', fontSize: '.73rem', cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 300, transition: 'all .15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#f0a050'; e.currentTarget.style.color = '#f0a050'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text2)'; }}>
              {s}
            </button>
          ))}
        </div>
      )}

      <div style={{ flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: '.6rem', alignItems: 'flex-end' }}>
          <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} placeholder="Ask Rico Nutrition anything…" rows={1}
            style={{ flex: 1, resize: 'none', padding: '.8rem 1rem', borderRadius: 'var(--radius-lg)', lineHeight: 1.5, minHeight: '46px', maxHeight: '120px', fontFamily: 'var(--font-body)', fontSize: '.85rem', background: 'var(--bg-card)', border: '1px solid var(--border2)', color: 'var(--text)', outline: 'none' }} />
          <button onClick={() => sendMessage()} disabled={!input.trim() || loading} style={{ padding: '.8rem 1.4rem', borderRadius: 'var(--radius-lg)', border: 'none', background: input.trim() && !loading ? '#f0a050' : 'var(--bg-elevated)', color: input.trim() && !loading ? '#080f18' : 'var(--text3)', fontFamily: 'var(--font-body)', fontSize: '.82rem', fontWeight: 500, cursor: input.trim() && !loading ? 'pointer' : 'default', height: '46px', flexShrink: 0, transition: 'all .15s' }}>
            Send →
          </button>
        </div>
        <div style={{ marginTop: '.4rem', fontSize: '.65rem', color: 'var(--text3)', textAlign: 'center' }}>Rico Nutrition uses your meal plan, food log and health data to give personalised advice.</div>
      </div>
      <style>{'@keyframes bounce { 0%,60%,100%{transform:translateY(0);opacity:.5} 30%{transform:translateY(-4px);opacity:1} }'}</style>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

function MealPlanHistory({ onLoad }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const [hist, current] = await Promise.all([loadMealPlanHistory(), loadMealPlan()]);
      // If no history but current plan exists, save it to history as a backfill
      if (hist.length === 0 && current && current.days) {
        await saveMealPlan(current);
        const hist2 = await loadMealPlanHistory();
        setHistory(hist2);
      } else {
        setHistory(hist);
      }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div style={{ color: 'var(--text3)', padding: '2rem 0' }}>Loading history…</div>;

  if (history.length === 0) return (
    <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text3)' }}>
      <div style={{ fontSize: '2rem', marginBottom: '1rem' }}><Icon name="clipboard" size={14} /></div>
      <div style={{ fontSize: '.85rem' }}>No previous meal plans yet. Generate your first plan to see history here.</div>
    </div>
  );

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', fontWeight: 500, color: 'var(--text)', marginBottom: '1.25rem' }}>Previous Meal Plans</h2>
      {history.map((plan, i) => {
        const date = plan.savedAt?.seconds ? new Date(plan.savedAt.seconds * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Unknown date';
        const days = plan.days?.length || 0;
        const themes = plan.days?.slice(0,3).map(d => d.theme).filter(Boolean).join(', ') || '';
        return (
          <div key={plan.id || i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem', marginBottom: '.65rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '.85rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.2rem' }}>Plan from {date}</div>
              <div style={{ fontSize: '.75rem', color: 'var(--text3)', fontWeight: 300 }}>{days} days · {themes}{themes ? '…' : ''}</div>
            </div>
            <button onClick={() => onLoad(plan)} style={{ padding: '.45rem 1rem', borderRadius: 'var(--radius)', border: '1px solid rgba(240,160,80,.3)', background: 'rgba(240,160,80,.08)', color: '#f0a050', fontSize: '.78rem', cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 500, flexShrink: 0 }}>
              Restore →
            </button>
          </div>
        );
      })}
    </div>
  );
}


// ── Water Tracker ─────────────────────────────────────────────────────────────
function WaterTracker({ nutritionProfile }) {
  const user = auth.currentUser;
  const today = new Date().toISOString().split('T')[0];
  const [glasses, setGlasses] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  // Target: 8 glasses (2L) by default, or 10 if active
  const target = nutritionProfile?.jobType === 'very_active' || nutritionProfile?.exerciseFrequency >= 5 ? 10 : 8;
  const mlPerGlass = 250;
  const totalMl = glasses * mlPerGlass;
  const pct = Math.min(100, Math.round((glasses / target) * 100));

  React.useEffect(() => {
    if (!user) return;
    getDoc(doc(db, 'users', user.uid, 'history', 'water', 'days', today))
      .then(snap => { if (snap.exists()) setGlasses(snap.data().glasses || 0); })
      .catch(() => {});
  }, []);

  async function updateGlasses(val) {
    const newVal = Math.max(0, val);
    setGlasses(newVal);
    setSaving(true);
    try {
      await setDoc(doc(db, 'users', user.uid, 'history', 'water', 'days', today), { glasses: newVal, date: today, updatedAt: serverTimestamp() });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch(e) { console.error(e); }
    setSaving(false);
  }

  const statusColor = pct >= 100 ? 'var(--success)' : pct >= 50 ? 'var(--warning)' : 'var(--danger)';
  const statusMsg = pct >= 100 ? 'Target reached! Great hydration today.' : pct >= 75 ? 'Almost there — one more glass!' : pct >= 50 ? 'Halfway there. Keep going.' : pct >= 25 ? 'Getting started — keep sipping.' : 'Start hydrating — your body needs it.';

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.4rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.3rem' }}><Icon name="droplet" size={14} /> Water Tracker</h2>
        <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300 }}>Track your daily water intake. Target: {target} glasses ({target * mlPerGlass}ml) per day.</p>
      </div>

      {/* Main display */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '2rem', textAlign: 'center', marginBottom: '1rem' }}>
        <div style={{ fontFamily: 'var(--font-head)', fontSize: '4rem', fontWeight: 600, color: statusColor, lineHeight: 1, marginBottom: '.25rem' }}>{glasses}</div>
        <div style={{ fontSize: '.82rem', color: 'var(--text3)', marginBottom: '1.5rem' }}>of {target} glasses · {totalMl}ml</div>

        {/* Progress bar */}
        <div style={{ height: 12, background: 'var(--bg-elevated)', borderRadius: 6, marginBottom: '1rem', overflow: 'hidden' }}>
          <div style={{ width: pct + '%', height: '100%', background: statusColor, borderRadius: 6, transition: 'width .5s ease' }} />
        </div>
        <div style={{ fontSize: '.78rem', color: statusColor, marginBottom: '1.5rem', fontWeight: 400 }}>{statusMsg}</div>

        {/* Glasses display */}
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '.5rem', marginBottom: '1.5rem' }}>
          {Array.from({ length: target }, (_, i) => (
            <div key={i} onClick={() => updateGlasses(i + 1)} style={{ fontSize: '1.6rem', cursor: 'pointer', opacity: i < glasses ? 1 : 0.25, transition: 'opacity .2s, transform .1s', transform: i < glasses ? 'scale(1.1)' : 'scale(1)' }}>
              <Icon name="droplet" size={14} />
            </div>
          ))}
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', gap: '.75rem', justifyContent: 'center', alignItems: 'center' }}>
          <button onClick={() => updateGlasses(glasses - 1)} disabled={glasses === 0} style={{ width: 44, height: 44, borderRadius: '50%', border: '1px solid var(--border2)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: '1.2rem', cursor: glasses === 0 ? 'not-allowed' : 'pointer', opacity: glasses === 0 ? .4 : 1 }}>−</button>
          <button onClick={() => updateGlasses(glasses + 1)} style={{ padding: '.65rem 1.75rem', borderRadius: 'var(--radius)', border: 'none', background: 'var(--primary)', color: '#080f18', fontFamily: 'var(--font-body)', fontSize: '.88rem', fontWeight: 500, cursor: 'pointer' }}>
            + Add glass {saved && '✓'}
          </button>
          <button onClick={() => updateGlasses(glasses + 1)} disabled={glasses >= target + 4} style={{ width: 44, height: 44, borderRadius: '50%', border: '1px solid var(--border2)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: '1.2rem', cursor: 'pointer' }}>+</button>
        </div>
      </div>

      {/* Info card */}
      <div style={{ padding: '1rem 1.25rem', background: 'rgba(0,174,239,0.04)', border: '1px solid rgba(0,174,239,0.12)', borderRadius: 'var(--radius-lg)', fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.7 }}>
        <Icon name="info" size={14} /> <strong style={{ color: 'var(--text)' }}>Why it matters:</strong> Staying hydrated directly affects your blood markers — particularly kidney function (creatinine, eGFR), uric acid levels and blood viscosity. Even mild dehydration impairs concentration and energy. Aim to drink consistently throughout the day rather than all at once.
      </div>
    </div>
  );
}

// ── Shopping List ─────────────────────────────────────────────────────────────
function ShoppingList({ mealPlan }) {
  const [copied, setCopied] = React.useState(false);
  const [filter, setFilter] = React.useState('all');

  if (!mealPlan || !mealPlan.days) {
    return (
      <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center', padding: '3rem 2rem' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}><Icon name="cart" size={14} /></div>
        <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', color: 'var(--text)', marginBottom: '.5rem' }}>No meal plan yet</h2>
        <p style={{ fontSize: '.82rem', color: 'var(--text3)' }}>Generate your 7-day meal plan first, then come back here for your shopping list.</p>
      </div>
    );
  }

  // Extract and consolidate all ingredients from the meal plan
  const rawIngredients = [];
  try {
    (mealPlan.days || []).forEach(day => {
      (day.meals || []).forEach(meal => {
        (meal.ingredients || []).forEach(ing => {
          // Ingredients might be strings or objects - handle both
          const ingStr = typeof ing === 'string' ? ing : (ing.name || ing.item || JSON.stringify(ing));
          if (ingStr && typeof ingStr === 'string' && ingStr.trim()) {
            rawIngredients.push(ingStr.trim());
          }
        });
      });
    });
  } catch(e) { console.error('Ingredient extraction error:', e); }

  // Consolidate duplicates and group by category
  const categories = {
    'Proteins': ['chicken', 'beef', 'fish', 'salmon', 'tuna', 'turkey', 'pork', 'lamb', 'egg', 'eggs', 'tofu', 'mince', 'steak', 'cod', 'prawns', 'shrimp', 'bacon'],
    'Dairy & Alternatives': ['milk', 'cheese', 'yogurt', 'yoghurt', 'butter', 'cream', 'cheddar', 'mozzarella', 'parmesan', 'feta', 'ricotta', 'oat milk', 'almond milk'],
    'Grains & Carbs': ['rice', 'pasta', 'bread', 'oats', 'flour', 'quinoa', 'noodles', 'tortilla', 'wrap', 'bagel', 'couscous', 'potato', 'sweet potato', 'lentils', 'chickpeas', 'beans'],
    'Fruit': ['apple', 'banana', 'berries', 'strawberry', 'blueberry', 'orange', 'lemon', 'lime', 'mango', 'avocado', 'grape', 'pear', 'peach', 'melon', 'tomato', 'tomatoes'],
    'Vegetables': ['spinach', 'broccoli', 'carrot', 'onion', 'garlic', 'pepper', 'courgette', 'zucchini', 'mushroom', 'celery', 'lettuce', 'kale', 'asparagus', 'cucumber', 'cabbage', 'leek', 'aubergine', 'eggplant'],
    'Oils, Sauces & Condiments': ['olive oil', 'coconut oil', 'soy sauce', 'vinegar', 'mustard', 'ketchup', 'mayo', 'honey', 'jam', 'pesto', 'tahini', 'sriracha', 'worcestershire'],
    'Herbs, Spices & Seasonings': ['salt', 'pepper', 'cumin', 'paprika', 'turmeric', 'oregano', 'basil', 'thyme', 'rosemary', 'coriander', 'ginger', 'chilli', 'cinnamon', 'cayenne', 'mixed herbs'],
    'Nuts, Seeds & Extras': ['almonds', 'walnuts', 'cashews', 'peanut', 'chia', 'flaxseed', 'sunflower seeds', 'stock', 'broth', 'protein powder'],
  };

  function categorise(ingredient) {
    const lower = ingredient.toLowerCase();
    for (const [cat, keywords] of Object.entries(categories)) {
      if (keywords.some(k => lower.includes(k))) return cat;
    }
    return 'Other';
  }

  // Group ingredients by category, deduplicating
  const grouped = {};
  try {
    rawIngredients.forEach(ingredient => {
      const cat = categorise(ingredient);
      if (!grouped[cat]) grouped[cat] = new Set();
      grouped[cat].add(ingredient);
    });
  } catch(e) { console.error('Grouping error:', e); }

  if (rawIngredients.length === 0) {
    return (
      <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center', padding: '3rem 2rem' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}><Icon name="cart" size={14} /></div>
        <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', color: 'var(--text)', marginBottom: '.5rem' }}>No ingredients found</h2>
        <p style={{ fontSize: '.82rem', color: 'var(--text3)' }}>Your meal plan may not contain ingredient lists. Try regenerating your meal plan.</p>
      </div>
    );
  }

  const catOrder = [...Object.keys(categories), 'Other'];
  const sortedGroups = catOrder.filter(c => grouped[c]);
  const filteredGroups = filter === 'all' ? sortedGroups : [filter];

  function buildPlainText() {
    let lines = ['SHOPPING LIST - Levare 7-Day Meal Plan', ''];
    sortedGroups.forEach(cat => {
      lines.push(cat.toUpperCase());
      [...grouped[cat]].sort().forEach(ing => { lines.push('  - ' + ing); });
      lines.push('');
    });
    return lines.join('\n');
  }

  function handleCopy() {
    navigator.clipboard.writeText(buildPlainText()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const totalItems = Object.values(grouped).reduce((sum, set) => sum + set.size, 0);

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.4rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.3rem' }}><Icon name="cart" size={14} /> Shopping List</h2>
          <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300 }}>{totalItems} items from your 7-day meal plan · grouped by category</p>
        </div>
        <button onClick={handleCopy} style={{ padding: '.6rem 1.25rem', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: copied ? 'var(--success)' : 'var(--bg-card)', color: copied ? '#080f18' : 'var(--text3)', fontFamily: 'var(--font-body)', fontSize: '.78rem', cursor: 'pointer', fontWeight: 500, transition: 'all .2s' }}>
          {copied ? '✓ Copied!' : '<Icon name="clipboard" size={14} /> Copy list'}
        </button>
      </div>

      {/* Category filter */}
      <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <button onClick={() => setFilter('all')} style={{ padding: '.3rem .75rem', borderRadius: 20, border: '1px solid', borderColor: filter === 'all' ? 'var(--primary)' : 'var(--border2)', background: filter === 'all' ? 'rgba(0,174,239,0.1)' : 'transparent', color: filter === 'all' ? 'var(--primary)' : 'var(--text3)', fontSize: '.7rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
          All ({totalItems})
        </button>
        {sortedGroups.map(cat => (
          <button key={cat} onClick={() => setFilter(cat)} style={{ padding: '.3rem .75rem', borderRadius: 20, border: '1px solid', borderColor: filter === cat ? 'var(--primary)' : 'var(--border2)', background: filter === cat ? 'rgba(0,174,239,0.1)' : 'transparent', color: filter === cat ? 'var(--primary)' : 'var(--text3)', fontSize: '.7rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
            {cat.split(' ')[0]} ({grouped[cat].size})
          </button>
        ))}
      </div>

      {/* Ingredient groups */}
      {filteredGroups.map(cat => (
        <div key={cat} style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem', marginBottom: '.75rem' }}>
          <div style={{ fontSize: '.68rem', fontWeight: 600, color: '#f0a050', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>{cat}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '.4rem' }}>
            {[...grouped[cat]].sort().map((ing, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.8rem', color: 'var(--text2)', padding: '.3rem 0', borderBottom: '1px solid var(--border2)' }}>
                <span style={{ color: 'var(--text3)', flexShrink: 0 }}>□</span>
                {ing}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{ padding: '.75rem 1rem', background: 'rgba(0,174,239,0.04)', border: '1px solid rgba(0,174,239,0.12)', borderRadius: 'var(--radius)', fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>
        <Icon name="info" size={14} /> Quantities are not shown as portion sizes vary. Check each recipe for exact amounts needed. Use "Copy list" to paste into your notes or share with someone.
      </div>
    </div>
  );
}


// ── Create a Meal ─────────────────────────────────────────────────────────────
function CreateMeal({ nutritionProfile }) {
  const [complexity, setComplexity] = React.useState('easy');
  const [mealType, setMealType] = React.useState('dinner');
  const [calories, setCalories] = React.useState('500');
  const [protein, setProtein] = React.useState('40');
  const [carbs, setCarbs] = React.useState('50');
  const [fat, setFat] = React.useState('15');
  const [customNotes, setCustomNotes] = React.useState('');
  const [generating, setGenerating] = React.useState(false);
  const [meal, setMeal] = React.useState(null);
  const [error, setError] = React.useState('');
  const [saved, setSaved] = React.useState(false);

  const complexityOptions = [
    { id: 'easy', label: 'Easy', desc: '3–4 ingredients, 15 mins', icon: 'check-circle' },
    { id: 'medium', label: 'Medium', desc: '5–7 ingredients, 30 mins', icon: 'warning' },
    { id: 'complex', label: 'Complex', desc: '8+ ingredients, 45+ mins', icon: 'x-circle' },
  ];

  const mealTypes = [
    { id: 'breakfast', label: 'Breakfast', icon: 'sun' },
    { id: 'lunch', label: 'Lunch', icon: 'sun' },
    { id: 'dinner', label: 'Dinner', icon: 'moon' },
    { id: 'snack', label: 'Snack', icon: 'apple' },
    { id: 'post-workout', label: 'Post-Workout', icon: 'dumbbell' },
  ];

  async function handleGenerate() {
    setGenerating(true);
    setError('');
    setMeal(null);
    setSaved(false);
    try {
      const restrictions = nutritionProfile?.dietaryRestrictions || 'none';
      const preferences = nutritionProfile?.favouriteMeals || '';
      const data = await apiPost('/api/create-meal', {
        complexity, mealType, calories, protein, carbs, fat, customNotes, restrictions, preferences
      });
      setMeal(data.meal);
    } catch(e) {
      setError('Failed to generate meal. Please try again.');
    }
    setGenerating(false);
  }

  async function handleSaveToLog() {
    if (!meal) return;
    try {
      const user = auth.currentUser;
      const today = new Date().toISOString().split('T')[0];
      const mealCals = meal.calories || parseInt(calories);
      const mealProt = meal.protein || parseInt(protein);
      const mealCarbs = meal.carbs || parseInt(carbs);
      const mealFat = meal.fat || parseInt(fat);
      await addDoc(collection(db, 'users', user.uid, 'history', 'nutrition', 'entries'), {
        date: today,
        description: meal.name,
        calories: mealCals,
        protein: mealProt,
        carbs: mealCarbs,
        fat: mealFat,
        mealType: mealType,
        items: [{ name: meal.name, calories: mealCals, protein: mealProt, carbs: mealCarbs, fat: mealFat, source: 'created' }],
        recordedAt: serverTimestamp()
      });
      setSaved(true);
    } catch(e) { console.error(e); }
  }

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.4rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.3rem' }}><Icon name="zap" size={14} /> Create a Meal</h2>
        <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300 }}>Set your macros and complexity, and Rico will generate a recipe tailored to you.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>

        {/* Meal type */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem' }}>
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>Meal Type</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
            {mealTypes.map(t => (
              <button key={t.id} onClick={() => setMealType(t.id)} style={{ display: 'flex', alignItems: 'center', gap: '.6rem', padding: '.5rem .75rem', borderRadius: 'var(--radius)', border: '1px solid', borderColor: mealType === t.id ? 'var(--warning)' : 'var(--border2)', background: mealType === t.id ? 'rgba(240,160,80,0.08)' : 'transparent', color: mealType === t.id ? 'var(--warning)' : 'var(--text3)', fontSize: '.8rem', cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'left', transition: 'all .15s' }}>
                <Icon name={t.icon} size={16} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Complexity */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem' }}>
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>Complexity</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', marginBottom: '1.25rem' }}>
            {complexityOptions.map(c => (
              <button key={c.id} onClick={() => setComplexity(c.id)} style={{ display: 'flex', alignItems: 'center', gap: '.75rem', padding: '.6rem .85rem', borderRadius: 'var(--radius)', border: '1px solid', borderColor: complexity === c.id ? 'var(--primary)' : 'var(--border2)', background: complexity === c.id ? 'rgba(0,174,239,0.08)' : 'transparent', cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'left', transition: 'all .15s' }}>
                <Icon name={c.icon} size={16} />
                <div>
                  <div style={{ fontSize: '.82rem', fontWeight: 500, color: complexity === c.id ? 'var(--primary)' : 'var(--text)' }}>{c.label}</div>
                  <div style={{ fontSize: '.68rem', color: 'var(--text3)', fontWeight: 300 }}>{c.desc}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Custom notes */}
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.4rem' }}>Any requests?</div>
          <textarea value={customNotes} onChange={e => setCustomNotes(e.target.value)} placeholder="e.g. high protein, no dairy, using chicken..." rows={2}
            style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.55rem .75rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.78rem', outline: 'none', resize: 'none', boxSizing: 'border-box' }} />
        </div>
      </div>

      {/* Macro targets */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.75rem', flexWrap: 'wrap', gap: '.5rem' }}>
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em' }}>Macro Targets</div>
          {(() => {
            const impliedCals = (parseInt(protein) * 4) + (parseInt(carbs) * 4) + (parseInt(fat) * 9);
            const diff = impliedCals - parseInt(calories);
            const ok = Math.abs(diff) < parseInt(calories) * 0.15;
            return (
              <div style={{ fontSize: '.7rem', color: ok ? 'var(--success)' : 'var(--warning)', fontWeight: 400 }}>
                {ok ? <Icon name='check-circle' size={13} /> : <Icon name='warning' size={13} />} Macros imply {impliedCals} kcal {!ok && `(${diff > 0 ? '+' : ''}${diff} vs target)`}
              </div>
            );
          })()}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '.75rem' }}>
          {[
            { label: 'Calories', value: calories, set: setCalories, unit: 'kcal', color: 'var(--warning)' },
            { label: 'Protein', value: protein, set: setProtein, unit: 'g', color: 'var(--primary)' },
            { label: 'Carbs', value: carbs, set: setCarbs, unit: 'g', color: 'var(--success)' },
            { label: 'Fat', value: fat, set: setFat, unit: 'g', color: '#a78bfa' },
          ].map(m => (
            <div key={m.label}>
              <div style={{ fontSize: '.62rem', fontWeight: 600, color: m.color, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '.3rem' }}>{m.label}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                <input type="number" value={m.value} onChange={e => m.set(e.target.value)} style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.45rem .6rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.88rem', outline: 'none' }} />
                <span style={{ fontSize: '.7rem', color: 'var(--text3)', whiteSpace: 'nowrap' }}>{m.unit}</span>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '.6rem', fontSize: '.68rem', color: 'var(--text3)', fontWeight: 300 }}>
          Formula: protein × 4 + carbs × 4 + fat × 9 = calories
        </div>
      </div>

      {/* Generate button */}
      <button onClick={handleGenerate} disabled={generating} style={{ width: '100%', padding: '.85rem', borderRadius: 'var(--radius-lg)', border: 'none', background: generating ? 'var(--bg-elevated)' : 'linear-gradient(135deg, #f0a050, #e8843a)', color: generating ? 'var(--text3)' : '#080f18', fontFamily: 'var(--font-body)', fontSize: '.95rem', fontWeight: 600, cursor: generating ? 'not-allowed' : 'pointer', marginBottom: '1rem', transition: 'all .2s' }}>
        {generating ? '<Icon name="zap" size={14} /> Rico is creating your meal…' : '<Icon name="zap" size={14} /> Generate My Meal'}
      </button>

      {error && <div style={{ padding: '.75rem 1rem', background: 'var(--danger-dim)', border: '1px solid var(--danger)', borderRadius: 'var(--radius)', color: 'var(--danger)', fontSize: '.78rem', marginBottom: '1rem' }}>{error}</div>}

      {/* Generated meal */}
      {meal && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', animation: 'fadeIn .4s ease' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap', gap: '.75rem' }}>
            <div>
              <h3 style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.25rem' }}>{meal.name}</h3>
              <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300 }}>{meal.description}</p>
            </div>
            <div style={{ display: 'flex', gap: '.5rem', flexShrink: 0 }}>
              <div style={{ padding: '.3rem .7rem', background: 'rgba(240,160,80,0.1)', border: '1px solid rgba(240,160,80,0.2)', borderRadius: 'var(--radius)', fontSize: '.7rem', color: 'var(--warning)', fontWeight: 500 }}>⏱ {meal.prepTime} prep · {meal.cookTime} cook</div>
            </div>
          </div>

          {/* Macros */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))', gap: '.5rem', marginBottom: '1.25rem' }}>
            {[['Calories', meal.calories, 'kcal', 'var(--warning)'], ['Protein', meal.protein, 'g', 'var(--primary)'], ['Carbs', meal.carbs, 'g', 'var(--success)'], ['Fat', meal.fat, 'g', '#a78bfa)']].map(([label, val, unit, color]) => (
              <div key={label} style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', padding: '.6rem .75rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1rem', fontWeight: 600, color }}>{val}{unit}</div>
                <div style={{ fontSize: '.62rem', color: 'var(--text3)', marginTop: '.1rem' }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Ingredients */}
          <div style={{ marginBottom: '1.1rem' }}>
            <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.6rem' }}>Ingredients</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
              {(meal.ingredients || []).map((ing, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.82rem', color: 'var(--text2)' }}>
                  <span style={{ color: 'var(--success)', flexShrink: 0 }}>•</span> {ing}
                </div>
              ))}
            </div>
          </div>

          {/* Method */}
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.6rem' }}>Method</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
              {(meal.method || []).map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: '.75rem', fontSize: '.82rem', color: 'var(--text2)', lineHeight: 1.5 }}>
                  <span style={{ color: 'var(--primary)', fontWeight: 600, flexShrink: 0, minWidth: 18 }}>{i + 1}.</span> {step}
                </div>
              ))}
            </div>
          </div>

          {meal.nutritionNotes && (
            <div style={{ padding: '.65rem .85rem', background: 'rgba(0,174,239,0.05)', border: '1px solid rgba(0,174,239,0.12)', borderRadius: 'var(--radius)', fontSize: '.75rem', color: 'var(--text3)', lineHeight: 1.6, marginBottom: '1rem' }}>
              <Icon name="info" size={14} /> {meal.nutritionNotes}
            </div>
          )}

          <div style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap' }}>
            <button onClick={handleGenerate} style={{ padding: '.6rem 1.25rem', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'transparent', color: 'var(--text3)', fontFamily: 'var(--font-body)', fontSize: '.78rem', cursor: 'pointer' }}>↻ Regenerate</button>
            <button onClick={handleSaveToLog} disabled={saved} style={{ padding: '.6rem 1.25rem', borderRadius: 'var(--radius)', border: 'none', background: saved ? 'var(--success)' : 'var(--primary)', color: '#080f18', fontFamily: 'var(--font-body)', fontSize: '.78rem', fontWeight: 500, cursor: saved ? 'default' : 'pointer' }}>
              {saved ? '✓ Added to food log' : '+ Add to food log'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Nutrition() {
  const [tab, setTab] = useState('plan');
  const [loading, setLoading] = useState(true);
  const [nutritionProfile, setNutritionProfile] = useState(null);
  const [mealPlan, setMealPlan] = useState(null);
  const [bloodData, setBloodData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [existingData, setExistingData] = useState({});
  const [todayEntries, setTodayEntries] = useState([]);

  useEffect(() => { loadAll(); loadTodayEntries(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [np, mp, blood, body, mental, profile, training] = await Promise.all([
        loadNutritionProfile(), loadMealPlan(),
        loadAllBloodMarkers(), loadLatestMetrics('body'),
        loadLatestMetrics('mental'), loadLatestMetrics('profile'),
        loadLatestMetrics('trainingProfile')
      ]);
      setNutritionProfile(np);
      setMealPlan(mp);
      setBloodData(blood);
      const age = profile?.dob ? calcAge(profile.dob) : null;
      setExistingData({ body: body || {}, mental: mental || {}, profile: profile || {}, training: training || {}, age });
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function loadTodayEntries() {
    try {
      const user = auth.currentUser;
      if (!user) return;
      const today = new Date().toISOString().split('T')[0];
      const q = query(collection(db, 'users', user.uid, 'history', 'nutrition', 'entries'), orderBy('recordedAt', 'desc'), limit(20));
      const snap = await getDocs(q);
      const entries = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(e => e.date === today);
      setTodayEntries(entries);
    } catch (e) { console.error('loadTodayEntries error:', e); }
  }

  async function generateMealPlan(np) {
    setGenerating(true);
    setGenError('');

    // Build health context from blood markers
    let healthNotes = '';
    if (bloodData) {
      if (bloodData.fastingGlucose && parseFloat(bloodData.fastingGlucose) >= 5.6) healthNotes += `Elevated fasting glucose (${bloodData.fastingGlucose} mmol/L) — prioritise low-GI carbs, reduce refined sugars, increase fibre.\n`;
      if (bloodData.totalCholesterol && parseFloat(bloodData.totalCholesterol) >= 5) healthNotes += `Elevated cholesterol (${bloodData.totalCholesterol} mmol/L) — include oily fish 2-3x/week, reduce saturated fat, add omega-3.\n`;
      if (bloodData.systolicBP && parseFloat(bloodData.systolicBP) >= 130) healthNotes += `Elevated blood pressure (${bloodData.systolicBP} mmHg) — reduce sodium, increase potassium-rich foods (bananas, sweet potato, spinach).\n`;
      if (bloodData.vitaminD && parseFloat(bloodData.vitaminD) < 50) healthNotes += `Low Vitamin D (${bloodData.vitaminD} nmol/L) — include oily fish, eggs, fortified foods.\n`;
      if (bloodData.ferritin && parseFloat(bloodData.ferritin) < 30) healthNotes += `Low ferritin (${bloodData.ferritin} ug/L) — prioritise iron-rich foods (red meat, lentils, spinach) with Vitamin C to aid absorption.\n`;
      if (bloodData.crp && parseFloat(bloodData.crp) > 3) healthNotes += `Elevated inflammation (CRP: ${bloodData.crp} mg/L) — include anti-inflammatory foods: oily fish, berries, turmeric, olive oil, leafy greens.\n`;
    }

    const prompt = `You are an expert nutritionist with 30 years of experience. Build a detailed, exciting 7-day meal plan for this person.

ATHLETE PROFILE:
Goal: ${np.primaryGoal?.replace('_', ' ')}
Age: ${np.age}, Sex: ${np.sex}, Weight: ${np.weight}kg, Height: ${np.height}cm
Job: ${np.jobType?.replace('_', ' ')}, Exercise: ${np.exerciseFrequency}x/week (${np.exerciseType})
Sleep: ${np.sleepHours}, Stress: ${np.stressLevel}, Alcohol: ${np.alcoholUnits || 0} units/week

CALCULATED TARGETS:
BMR: ${np.bmr} kcal
TDEE: ${np.tdee} kcal  
Daily calorie target: ${np.targetCalories} kcal
Protein: ${np.proteinTarget}g | Carbs: ${np.carbTarget}g | Fat: ${np.fatTarget}g

FOOD PREFERENCES:
Favourite meals: ${np.favouriteMeals}
Dislikes: ${np.dislikedFoods || 'None stated'}
Dietary restrictions: ${np.dietaryRestrictions || 'None'}
Cooking style: ${np.cookingStyle}
Food adventurousness: ${np.foodAdventurousness}/10

SNACK HABITS:
Current snacks: ${np.currentSnacks}
Snacks because of: ${np.snackReason}
Preference: ${np.snackPreference}
Late night snacking: ${np.lateNightSnacking}

HEALTH DATA CONSIDERATIONS:
${healthNotes || 'No specific health flags — standard healthy eating approach.'}

RULES:
- Each day must hit calorie and macro targets
- Protein must be distributed across ALL meals — do not leave it all to dinner
- Give each day a fun theme (e.g. Mediterranean Monday, Taco Tuesday)
- No boring chicken and broccoli unless explicitly requested
- Include 2 treat meals per week that feel indulgent but are secretly low calorie
- Flag meals good for batch cooking
- If alcohol listed, factor into relevant day calories
- Include detailed recipes with ingredients and method steps
- Make it exciting and genuinely delicious

Return ONLY valid JSON, no markdown:
{
  "days": [
    {
      "theme": "Mediterranean Monday",
      "totalCalories": 2000,
      "totalProtein": 150,
      "totalCarbs": 200,
      "totalFat": 65,
      "meals": [
        {
          "mealType": "breakfast",
          "name": "Greek Yoghurt Power Bowl",
          "calories": 420,
          "protein": 35,
          "carbs": 45,
          "fat": 12,
          "description": "A protein-packed start that keeps you full until lunch",
          "prepTime": "5 mins",
          "cookTime": "0 mins",
          "servings": 1,
          "batchCook": false,
          "treatMeal": false,
          "ingredients": [{"item": "Greek yoghurt (0% fat)", "amount": "200g"}, {"item": "Mixed berries", "amount": "100g"}],
          "method": ["Add yoghurt to bowl", "Top with berries and granola", "Drizzle with honey"]
        }
      ],
      "snackSwaps": [
        {"original": "Crisps", "originalCals": 180, "swap": "Rice cakes with hummus", "swapCals": 95}
      ]
    }
  ],
  "supplements": [
    {"name": "Creatine Monohydrate", "dose": "5g daily", "timing": "Any time, consistency matters", "reason": "Improves strength and body composition regardless of goal"}
  ],
  "personalRules": [
    "Rule 1 specific to this person",
    "Rule 2 specific to this person"
  ],
  "hydrationTarget": "3.2 litres",
  "hydrationTips": ["Tip 1", "Tip 2"]
}`;

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 120000);
      const data = await apiPost('/api/generate-meal-plan', { prompt });
      clearTimeout(timeout);
      const plan = typeof data.mealPlan === 'string' ? JSON.parse(data.mealPlan) : data.mealPlan;
      await saveMealPlan(plan);
      setMealPlan(plan);
    } catch (e) {
      console.error(e);
      if (e.name === 'AbortError') setGenError('Timed out — please try again in 30 seconds.');
      else setGenError('Generation failed. Please try again.');
    }
    setGenerating(false);
  }

  if (loading) return <div style={{ padding: '2rem 0', color: 'var(--text3)' }}>Loading your nutrition data…</div>;

  if (!nutritionProfile?.setupComplete) {
    return <NutritionSetup onComplete={np => { setNutritionProfile(np); generateMealPlan(np); setTab('plan'); }} existingData={existingData} />;
  }

  const TABS = [
    { id: 'plan', label: 'My Meal Plan', icon: 'utensils' },
    { id: 'log', label: 'Food Log', icon: 'edit' },
    { id: 'water', label: 'Water', icon: 'droplet' },
    { id: 'shopping', label: 'Shopping List', icon: 'cart' },
    { id: 'create', label: 'Create a Meal', icon: 'zap' },
    { id: 'rico', label: 'Rico Nutrition', icon: 'apple' },
    { id: 'history', label: 'Plan History', icon: 'clipboard' },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <style>{`
        .nutr-tabs::-webkit-scrollbar { display: none; }
        @media (max-width: 480px) {
          .nutr-food-log-header { flex-direction: column !important; align-items: flex-start !important; }
          .nutr-gamification { flex-wrap: wrap; }
        }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '.75rem', marginBottom: '.75rem' }}>
          <div>
            <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.6rem', fontWeight: 600, color: 'var(--text)', letterSpacing: '-.02em', marginBottom: '.2rem' }}>My Nutrition</h1>
            <p style={{ fontSize: '.77rem', color: 'var(--text3)', fontWeight: 300 }}>
              {nutritionProfile.targetCalories} kcal target · {nutritionProfile.proteinTarget}g protein · {nutritionProfile.primaryGoal?.replace('_', ' ')}
            </p>
          </div>
          <button onClick={() => setNutritionProfile(null)} style={{ padding: '.35rem .85rem', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'transparent', color: 'var(--text3)', fontSize: '.73rem', cursor: 'pointer', fontFamily: 'var(--font-body)', flexShrink: 0 }}>Edit Profile</button>
        </div>
        <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
          {[['Target', nutritionProfile.targetCalories+' kcal', '#f0a050'],['Protein', nutritionProfile.proteinTarget+'g', 'var(--success)'],['Carbs', nutritionProfile.carbTarget+'g', 'var(--primary)'],['Fat', nutritionProfile.fatTarget+'g', '#a78bfa']].map(([l,v,c]) => (
            <div key={l} style={{ padding: '.3rem .65rem', borderRadius: '20px', background: c+'18', border: `1px solid ${c}44`, fontSize: '.7rem', fontWeight: 500, color: c, whiteSpace: 'nowrap' }}>
              <span style={{ opacity: .75 }}>{l}: </span>{v}
            </div>
          ))}
        </div>
      </div>

      {genError && (
        <div style={{ padding: '.75rem 1rem', background: 'var(--danger-dim)', border: '1px solid rgba(240,80,80,.2)', borderRadius: 'var(--radius)', color: 'var(--danger)', fontSize: '.78rem', marginBottom: '1rem' }}>{genError}</div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border2)', marginBottom: '1.75rem', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '.6rem 1rem', fontSize: '.8rem', color: tab === t.id ? '#f0a050' : 'var(--text3)', cursor: 'pointer', border: 'none', background: 'none', borderBottom: tab === t.id ? '2px solid #f0a050' : '2px solid transparent', marginBottom: '-1px', fontFamily: 'var(--font-body)', fontWeight: tab === t.id ? 500 : 400, transition: 'color .15s', display: 'flex', alignItems: 'center', gap: '.3rem', flexShrink: 0, whiteSpace: 'nowrap' }}>
            <Icon name={t.icon} size={14} />{t.label}
          </button>
        ))}
      </div>

      {tab === 'plan' && (
        <MealPlanView
          mealPlan={mealPlan}
          nutritionProfile={nutritionProfile}
          generating={generating}
          onRegenerate={() => generateMealPlan(nutritionProfile)}
        />
      )}
      {tab === 'log' && <FoodLog nutritionProfile={nutritionProfile} />}
      {tab === 'water' && <WaterTracker nutritionProfile={nutritionProfile} />}
      {tab === 'shopping' && <ShoppingList mealPlan={mealPlan} />}
      {tab === 'create' && <CreateMeal nutritionProfile={nutritionProfile} />}
      {tab === 'rico' && <RicoNutrition nutritionProfile={nutritionProfile} bloodData={bloodData} mealPlan={mealPlan} todayEntries={todayEntries} />}
      {tab === 'history' && <MealPlanHistory onLoad={plan => { setMealPlan(plan); setTab('plan'); }} />}
    </div>
  );
}
